import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { cloneDeep } from 'lodash-es';
import { map } from 'rxjs/operators';
import { DialogData } from '../../../domain/common.model';
import { FrontierConfiguration, FrontierRequest } from '../../../domain/frontier.domain';
import { Offer } from '../../../domain/offer.domain';
import { FrontierService } from '../service/frontier.service';
import { OfferService } from '../service/offer.service';
import { DialogAction } from './dialog.action';
import { FrontierAction } from './frontier.action';
import { OfferStateModel } from './offer.state';
import { RouterAction } from './router.action';

export class FrontierStateModel {
    request: FrontierRequest;
    offer: Offer;
}

@State<FrontierStateModel>({
    name: 'frontier',
    defaults: {
        request: new FrontierRequest(),
        offer: null,
    }
})
@Injectable()
export class FrontierState {

    constructor(private frontierService: FrontierService,
                private offerService: OfferService,
    ) {
    }

    @Selector()
    static getRequest(state: FrontierStateModel) {
        return state.request;
    }

    @Selector()
    static getOffer(state: FrontierStateModel) {
        return state.offer;
    }

    @Action(FrontierAction.Cleanup)
    cleanup(context: StateContext<FrontierStateModel>) {
        const request = new FrontierRequest();
        request.party.mainAddress.country = {code: 'UA'};
        request.party.correspondenceAddress.country = {code: 'UA'};
        return context.patchState({request});
    }

    @Action(FrontierAction.GetOne)
    getOne(context: StateContext<OfferStateModel>, action: FrontierAction.GetOne) {
        return this.offerService.getOne(action.id)
            .pipe(map(offer => context.patchState(this.parseOffer(offer))));
    }

    @Action(FrontierAction.Create)
    create(context: StateContext<OfferStateModel>, action: FrontierAction.Create) {
        return this.frontierService.create(action.request)
            .pipe(map(result => {
                context.dispatch(new RouterAction.Navigate(['/frontier', result.id, 'summary'], {replaceUrl: true}))
            }));
    }

    @Action(FrontierAction.Update)
    update(context: StateContext<OfferStateModel>, action: FrontierAction.Update) {
        return this.frontierService.update(context.getState().offer.id, action.request)
            .pipe(map(result => {
                context.dispatch(new RouterAction.Navigate(['/frontier', result.id, 'summary'], {replaceUrl: true}))
            }));
    }

    @Action(FrontierAction.Issue)
    issue(context: StateContext<OfferStateModel>, action: FrontierAction.Issue) {
        const data = new DialogData();
        data.titleLabel = 'POLICY_DIALOG.HEADER';
        data.descriptionLabel = 'CONFIRMATION.ISSUE_POLICY_FROM_OFFER';
        data.onConfirm = () => {
            this.frontierService.issue(action.offerId, action.request)
                .subscribe(policies => context.dispatch(new RouterAction.Navigate(['/policy', policies[0].offerId])));
        };
        return context.dispatch(new DialogAction.OpenGeneric(data));
    }

    private parseOffer(offer: Offer): Partial<FrontierStateModel> {
        const request = {
            party: offer.partyList[0],
            location: offer.additionalData.location,
            contactData: offer.additionalData.contactData,
            insuranceStartDate: (offer.productList[0].configuration as FrontierConfiguration).insuranceStartDate,
            insurancePeriod: (offer.productList[0].configuration as FrontierConfiguration).insurancePeriod,
            vehicleDefinition: (offer.productList[0].configuration as FrontierConfiguration).vehicleDefinition,
            vehicleOriginCountry: (offer.productList[0].configuration as FrontierConfiguration).vehicleOriginCountry,
            paymentType: (offer.productList[0].configuration as FrontierConfiguration).paymentDefinition.paymentType,
        } as FrontierRequest;
        return {
            offer: cloneDeep(offer),
            request: cloneDeep(request),
        };
    }
}
